import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  Output,
  ViewChild,
  OnInit,
  OnDestroy,
  AfterViewInit,
  OnChanges,
  SimpleChanges,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
} from '@angular/core';
import { Router } from '@angular/router';
import { trigger, state, style, transition, animate, query, group } from '@angular/animations';
import { StorageService } from '../../../../shared/services/storage.service';
import { NavigationTabsApiService, NavigationTab } from '../../../../shared/services/navigation-tabs-api.service';
import { LanguageService } from '../../../../shared/services/language.service';
import { PerformanceDataService } from '../../../../shared/services/performance-data.service';
import { ImageOptimizationService } from '../../../../shared/services/image-optimization.service';
import { take, takeUntil, finalize, timeout } from 'rxjs';
import { Subject } from 'rxjs';

export interface DesktopMenuSection {
  tab: NavigationTab;
  categories: any[];
  index: number;
}

export function filterVisibleCategories(categories: any[]): any[] {
  return categories
    .filter(category => category?.show !== false)
    .map(category => ({
      ...category,
      children: Array.isArray(category.children) ? filterVisibleCategories(category.children) : category.children,
    }));
}

interface NewProductCard {
  product: any;
  image: string;
  category: string;
  reference: string;
  producer: string;
  badge: 'NEW' | 'LATEST';
  availability: { label: string; className: string } | null;
  pricing: { price: number | null; originalPrice: number | null; discount: number };
}

export function buildDesktopCategorySections(
  parent: NavigationTab | null,
  categories: any[],
): DesktopMenuSection[] {
  const tabs = parent?.children?.length
    ? parent.children
    : parent
      ? [parent]
      : [];
  const categoriesById = new Map(categories.map(category => [Number(category.id), category]));
  const sections: DesktopMenuSection[] = [];

  tabs.forEach(tab => {
    const assignedCategories = tab.categoryIds?.length
      ? tab.categoryIds
        .map(categoryId => categoriesById.get(Number(categoryId)))
        .filter((category): category is any => !!category)
      : parent?.children?.length
        ? []
        : categories;

    assignedCategories.forEach(category => {
      const categoryId = Number(category.id);
      sections.push({
        tab: {
          ...tab,
          id: categoryId,
          name: category.label || category.name,
          categoryIds: [categoryId],
        },
        categories: [category],
        index: sections.length,
      });
    });
  });

  return sections;
}

@Component({
  selector: 'app-mega-menu',
  standalone: false,
  templateUrl: './mega-menu.component.html',
  styleUrl: './mega-menu.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('slideAnimation', [
      state('void', style({
        opacity: 0,
        visibility: 'hidden'
      })),
      state('*', style({
        opacity: 1,
        visibility: 'visible'
      })),
      transition('void => *', [
        style({ visibility: 'visible', opacity: 0 }),
        group([
          animate('180ms ease-out', style({ opacity: 1 })),
          query('.mega-menu-content', [
            style({ transform: 'translateY(-12px) scale(0.985)' }),
            animate('260ms cubic-bezier(0.25, 0.46, 0.45, 0.94)', style({
              transform: 'translateY(0) scale(1)'
            }))
          ], { optional: true })
        ])
      ]),
      transition('* => void', [
        group([
          animate('160ms ease-in', style({ opacity: 0 })),
          query('.mega-menu-content', [
            animate('160ms ease-in', style({ transform: 'translateY(-8px) scale(0.99)' }))
          ], { optional: true })
        ])
      ])
    ]),
    trigger('mobileSlide', [
      state('closed', style({
        visibility: 'hidden',
        opacity: 0
      })),
      state('open', style({
        visibility: 'visible',
        opacity: 1
      })),
      transition('closed => open', [
        style({ visibility: 'visible' }),
        animate('400ms cubic-bezier(0.25, 0.46, 0.45, 0.94)')
      ]),
      transition('open => closed', [
        animate('300ms cubic-bezier(0.55, 0.085, 0.68, 0.53)', style({
          opacity: 0
        })),
        style({ visibility: 'hidden' })
      ])
    ]),
    trigger('expandCollapse', [
      transition(':enter', [
        style({
          height: '0px',
          opacity: 0,
          overflow: 'hidden',
          transform: 'translateY(-10px)'
        }),
        animate('300ms cubic-bezier(0.25, 0.46, 0.45, 0.94)', style({
          height: '*',
          opacity: 1,
          transform: 'translateY(0)'
        }))
      ]),
      transition(':leave', [
        style({
          height: '*',
          opacity: 1,
          overflow: 'hidden',
          transform: 'translateY(0)'
        }),
        animate('250ms cubic-bezier(0.55, 0.085, 0.68, 0.53)', style({
          height: '0px',
          opacity: 0,
          transform: 'translateY(-10px)'
        }))
      ])
    ]),
    trigger('slideDown', [
      transition(':enter', [
        style({
          transform: 'translateY(-100%)',
          opacity: 0
        }),
        animate('300ms cubic-bezier(0.25, 0.46, 0.45, 0.94)',
          style({
            transform: 'translateY(0)',
            opacity: 1
          })
        )
      ]),
      transition(':leave', [
        animate('250ms cubic-bezier(0.55, 0.085, 0.68, 0.53)',
          style({
            transform: 'translateY(-100%)',
            opacity: 0
          })
        )
      ])
    ])
  ]
})
export class MegaMenuComponent implements OnInit, OnDestroy, AfterViewInit, OnChanges {
  @ViewChild('desktopContentPane') private desktopContentPane?: ElementRef<HTMLElement>;
  @ViewChild('newProductsTrack') private newProductsTrack?: ElementRef<HTMLElement>;

  @Input() isDrawerOpen: boolean = false;
  @Input() menuSelection: 'CATEGORIES' | 'SALE' | 'NEW' = 'CATEGORIES';
  @Input() currentUser: any = null;
  @Input() isUserLoggedIn: boolean = false;
  @Input() selectedCountry: any = null;
  protected titles: NavigationTab[] = [];
  categories: any[] = [];
  hoveredSubcategory: any;
  hoveredCategory: any = null;
  selectedSubTab: NavigationTab | null = null;
  desktopSections: DesktopMenuSection[] = [];
  desktopCategoryFilter = '';
  desktopCategoryCounts = new Map<number, number>();
  currentCategory: any = null;
  selectedNewProduct: any = null;
  showNewProductPreview = false;
  newProducts: any[] = [];
  newProductCards: NewProductCard[] = [];
  newProductsCanScrollLeft = false;
  newProductsCanScrollRight = false;
  newProductsPageCount = 1;
  newProductsActivePage = 0;
  isLoadingNewProducts = false;
  newProductsError = false;

  get newProductsPages(): number[] {
    return Array.from({ length: this.newProductsPageCount }, (_, index) => index);
  }

  get desktopParentTabs(): NavigationTab[] {
    return this.titles.filter(tab => tab.id !== -1 && tab.id !== -2 && tab.name !== 'SALE' && tab.name !== 'NEW');
  }

  get isPromoMenuSelection(): boolean {
    return this.menuSelection === 'SALE' || this.menuSelection === 'NEW';
  }

  get isMenuVisible(): boolean {
    return this.isDrawerOpen;
  }

  getMenuTabLabel(name: string): string {
    if (name === 'SALE') {
      return 'Promocje';
    }
    if (name === 'NEW') {
      return 'Nowości';
    }
    return name;
  }

  // Sales tab for discounted products
  salesTab: NavigationTab & { discountedSubcategories?: any[] } = {
    id: -1,
    name: 'SALE',
    description: 'Kategorie produktów objętych promocją',
    isActive: true,
    order: 999,
    categoryIds: [],
    createdAt: '',
    updatedAt: '',
    discountedSubcategories: []
  };

  // New tab for new products
  newTab: NavigationTab & { newSubcategories?: any[] } = {
    id: -2,
    name: 'NEW',
    description: 'Kategorie nowych produktów',
    isActive: true,
    order: 998,
    categoryIds: [],
    createdAt: '',
    updatedAt: '',
    newSubcategories: []
  };

  // Loading states
  isLoading = false;

  // Legacy image properties retained for the disabled gallery template.
  currentSlideIndex: number = 0;
  sliderImages: string[] = [];
  firstImage: { imageUrl?: string; altText?: string } | null = null;
  apiSliderImages: Array<{ altText?: string }> = [];
  private slideInterval: any;
  private touchStartX: number = 0;
  private touchEndX: number = 0;
  private isHovering: boolean = false;

  @Output() close: EventEmitter<void> = new EventEmitter<void>();
  @Output() toggleDrawerEmitter: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() profileClick: EventEmitter<void> = new EventEmitter<void>();
  @Output() countryClick: EventEmitter<Event> = new EventEmitter<Event>();

  // Responsive utilities
  isDesktop = false;
  isMobile = false;

  // Mobile subcategories expansion state
  expandedCategories: Set<string> = new Set();

  // Mobile sidebar category expansion state
  expandedSidebarCategories: Set<string> = new Set();

  // Language management
  currentLanguage: string = 'pl';

  // Animation state management
  isAnimating: boolean = false;

  // Search functionality
  searchQuery: string = '';

  // Country selector
  isCountrySelectorOpen: boolean = false;

  // Auth dropdown
  isAuthDropdownOpen: boolean = false;

  // Cleanup subject
  private destroy$ = new Subject<void>();
  private newProductsLoadId = 0;

  @HostListener('document:click', ['$event'])
  public onClick(event: Event) {
    const target = event.target as HTMLElement;
    const clickedInside = this.elementRef.nativeElement.contains(target);

    // Keep the menu open when clicking any of its desktop trigger buttons.
    const menuToggle = document.querySelector('app-menu-toggle');
    const desktopMenuActions = document.querySelector('.nav-category-actions');
    const clickedOnMenuTrigger =
      (menuToggle && menuToggle.contains(target))
      || (desktopMenuActions && desktopMenuActions.contains(target));

    if (!clickedInside && !clickedOnMenuTrigger && this.isDrawerOpen) {
      this.close.emit();
    }
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.isDrawerOpen) {
      this.close.emit();
    }
  }

  @HostListener('window:resize', ['$event'])
  onResize() {
    this.updateResponsiveStates();
  }

  @Output() onCategoryClickEmitter: EventEmitter<any> = new EventEmitter<any>();

  constructor(
    private readonly storageService: StorageService,
    private elementRef: ElementRef,
    private router: Router,
    private navigationTabsApiService: NavigationTabsApiService,
    private languageService: LanguageService,
    private performanceDataService: PerformanceDataService,
    private imageOptimizationService: ImageOptimizationService,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    this.loadInitialData();
    this.updateResponsiveStates();

    this.languageService.languageChange$
      .pipe(takeUntil(this.destroy$))
      .subscribe(language => {
        const languageChanged = this.currentLanguage !== language;
        this.currentLanguage = language;
        if (languageChanged && this.titles.some(tab => tab.id === this.newTab.id)) {
          this.newProducts = [];
          this.newProductCards = [];
          this.newProductsError = false;
          this.newTab.newSubcategories = [];
          this.newTab.categoryIds = [];
          if (this.menuSelection === 'NEW') {
            this.filteredCategories = [];
          }
          this.updateNewTab().catch(error => {
            this.newProductsError = true;
            console.error('Could not refresh menu new products after language change', error);
            this.cdr.markForCheck();
          });
        }
        this.cdr.markForCheck();
      });
  }

  ngAfterViewInit(): void {
    this.updateResponsiveStates();
  }

  ngOnDestroy(): void {
    if (this.slideInterval) {
      clearInterval(this.slideInterval);
    }
    // Clear cache to free memory
    this.categoriesCache.clear();

    this.destroy$.next();
    this.destroy$.complete();
  }

  // Load data using performance service (which combines individual API calls)
  private loadInitialData(): void {
    this.isLoading = true;

    // Use performance service which combines all API calls with caching
    this.performanceDataService.getInitialData()
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.isLoading = false;
          // Ensure first tab is selected on desktop after loading completes
          if (window.innerWidth >= 768) {
            this.ensureFirstTabSelected();
          }
          this.cdr.markForCheck();
        })
      )
      .subscribe({
        next: async (data) => {
          // Set navigation tabs
          if (data.navigationTabs && data.navigationTabs.length > 0) {
            this.titles = data.navigationTabs;

            // For desktop, auto-select first tab and sub-tab for better UX
            // For mobile, start with clean state to show main categories first
            if (window.innerWidth >= 768) { // Desktop breakpoint
              this.hoveredCategory = this.titles[0];
              if (this.titles[0].children && this.titles[0].children.length > 0) {
                this.selectedSubTab = this.titles[0].children[0];
                this.currentCategory = this.titles[0].children[0];
              } else {
                this.currentCategory = this.titles[0];
              }
            } else {
              // Mobile: start with clean state
              this.hoveredCategory = null;
              this.selectedSubTab = null;
              this.currentCategory = null;
            }
            this.applyMenuSelection();
          }

          // Set categories
          if (data.categories && data.categories.length > 0) {
            this.categories = filterVisibleCategories(data.categories);
            this.updateDesktopSections();

            // Update SALES tab after categories are loaded
            const salesUpdate = this.updateSalesTab().catch(error => {
              // Silent error handling for sales tab update
              console.error('Could not prepare menu promotions', error);
            });

            // Update NEW tab after categories are loaded
            const newProductsUpdate = this.updateNewTab().catch(error => {
              // Silent error handling for new tab update
              console.error('Could not prepare menu new products', error);
            });

            // Pre-populate cache for better performance
            this.prePopulateCache();
            // Set initial filtered categories
            this.updateFilteredCategories();

            await Promise.all([salesUpdate, newProductsUpdate]);
            this.cdr.markForCheck();
          } else {
            this.getCategories();
          }
        },
        error: (error) => {
          // Fallback to individual API calls
          this.loadNavigationTabs();
          this.getCategories();
        }
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['menuSelection']) {
      this.applyMenuSelection();
      if (changes['menuSelection'].currentValue === 'NEW') {
        requestAnimationFrame(() => {
          if (this.newProductsTrack) {
            this.newProductsTrack.nativeElement.scrollLeft = 0;
            this.newProductsActivePage = 0;
            this.updateNewProductsScrollState();
          }
        });
      }
    }

    if (changes['isDrawerOpen']) {
      const isOpening = changes['isDrawerOpen'].currentValue;

      if (isOpening) {
        // Check if we're on desktop or mobile
        const isDesktop = window.innerWidth >= 768;

        if (isDesktop) {
          // Desktop: Ensure first tab is selected when opening
          this.ensureFirstTabSelected();
        } else {
          // Mobile: Reset mobile navigation state
          this.resetMobileNavigationState();
        }

        this.isAnimating = true;
        // Reset animation flag after animation completes
        setTimeout(() => {
          this.isAnimating = false;
        }, 300);
      } else {
        // Sidebar is closing
        this.expandedSidebarCategories.clear();
        this.isAnimating = true;
        setTimeout(() => {
          this.isAnimating = false;
        }, 300);
      }
    }
  }

  private loadNavigationTabs(): void {
    this.navigationTabsApiService
      .getMainTabsWithChildren()
      .pipe(take(1))
      .subscribe({
        next: (tabs: NavigationTab[]) => {
          // Clear cache when navigation tabs change
          this.categoriesCache.clear();

          this.titles = tabs;
          if (tabs.length > 0) {
            this.hoveredCategory = tabs[0];
            // If the first main tab has children, select the first child as default
            if (tabs[0].children && tabs[0].children.length > 0) {
              this.selectedSubTab = tabs[0].children[0];
              this.currentCategory = tabs[0].children[0];
            } else {
              this.currentCategory = tabs[0];
            }
          }
          this.applyMenuSelection();
          this.updateDesktopSections();

          // Update filtered categories after navigation tabs are loaded
          this.updateFilteredCategories();
          this.cdr.markForCheck();
        },
        error: (error) => {
          // Fallback to default categories tab
          this.titles = [{ id: 1, name: 'Kategorie', isActive: true, order: 0, createdAt: '', updatedAt: '' }];
          this.hoveredCategory = this.titles[0];
          this.updateDesktopSections();
        }
      });
  }

  getCategories() {
    this.storageService
      .getProductCategoriesByLevel(1, 100)
      .pipe(take(1))
      .subscribe({
        next: async (response: any) => {
          // Clear cache when categories change
          this.categoriesCache.clear();

          this.categories = filterVisibleCategories(response.data);
          this.updateDesktopSections();

          // Update SALES tab after categories are loaded
          const salesUpdate = this.updateSalesTab().catch(error => {
            // Silent error handling for sales tab update
            console.error('Could not prepare menu promotions', error);
          });

          // Update NEW tab after categories are loaded
          const newProductsUpdate = this.updateNewTab().catch(error => {
            // Silent error handling for new tab update
            console.error('Could not prepare menu new products', error);
          });

          // Pre-populate cache for better performance
          this.prePopulateCache();
          // Set initial filtered categories
          this.updateFilteredCategories();
          this.cdr.markForCheck();
          await Promise.all([salesUpdate, newProductsUpdate]);
        },
        error: (error) => {
          // Silent error handling
        }
      });
  }



  toggleDrawer() {
    this.toggleDrawerEmitter.emit(this.isDrawerOpen);
  }

  onCategoryClick(category: any) {
    if (!category) return;

    this.desktopContentPane?.nativeElement.scrollTo({ top: 0, behavior: 'smooth' });

    if (this.hoveredCategory?.id === category.id && this.hoveredCategory?.name === category.name) {
      return;
    }

    this.hoveredCategory = category;
    this.updateDesktopSections();
    this.selectedSubTab = null; // Reset selected sub-tab when switching main tabs

    // If this main tab has children, select the first child for categories
    if (category.children && category.children.length > 0) {
      this.selectedSubTab = category.children[0];
      this.currentCategory = category.children[0];
    } else {
      this.currentCategory = category;
    }

    this.expandedCategories.clear(); // Reset expansions when switching tabs
    this.expandedSidebarCategories.clear(); // Reset sidebar expansions too

    // Update filtered categories immediately
    this.updateFilteredCategories();

    this.cdr.markForCheck(); // Manual change detection
  }

  private applyMenuSelection(): void {
    const category = this.menuSelection === 'CATEGORIES'
      ? this.desktopParentTabs[0]
      : this.titles.find(tab => tab.name === this.menuSelection);

    if (category) {
      this.onCategoryClick(category);
    }
  }

  getDesktopCategoryQueryParams(categoryId: number): Record<string, string | number> {
    const queryParams: Record<string, string | number> = { category: categoryId };
    if (this.menuSelection === 'SALE') {
      queryParams['discount'] = 'true';
    } else if (this.menuSelection === 'NEW') {
      queryParams['isNew'] = 'true';
    }
    return queryParams;
  }

  onSubTabClick(subTab: NavigationTab): void {
    // Skip if same sub-tab to avoid unnecessary work
    if (this.selectedSubTab?.id === subTab.id) {
      return;
    }

    this.selectedSubTab = subTab;
    this.currentCategory = subTab;
    this.expandedCategories.clear(); // Reset expansions when switching sub-tabs
    this.expandedSidebarCategories.clear(); // Reset sidebar expansions too

    // Update filtered categories immediately
    this.updateFilteredCategories();

    this.cdr.markForCheck(); // Manual change detection
  }

  private updateDesktopSections(): void {
    const parent = this.hoveredCategory;
    const sections: NavigationTab[] = parent?.children?.length
      ? parent.children
      : parent
        ? [parent]
        : [];

    if (parent?.name === 'SALE' || parent?.name === 'NEW') {
      this.desktopSections = sections.map((tab, index) => ({
        tab,
        categories: parent.name === 'SALE'
          ? this.salesTab.discountedSubcategories || []
          : this.newTab.newSubcategories || [],
        index,
      }));
    } else {
      this.desktopSections = buildDesktopCategorySections(parent, this.categories);
    }

    this.desktopCategoryFilter = [...new Set(
      this.desktopSections.flatMap(section =>
        section.categories
          .map(category => String(category.id))
          .filter(categoryId => categoryId !== 'undefined' && categoryId !== 'null')
      )
    )].join(',');

    this.desktopCategoryCounts.clear();
    for (const category of this.desktopSections.flatMap(section => section.categories)) {
      this.desktopCategoryCounts.set(category.id, this.getTotalProductCount(category));
    }
  }

  formatSectionNumber(index: number): string {
    return String(index + 1).padStart(2, '0');
  }

  trackByDesktopSection(_index: number, section: DesktopMenuSection): string {
    return `${section.tab.id}:${section.categories[0]?.id ?? 'empty'}`;
  }

  // Cached data and performance optimizations
  private categoriesCache: Map<string, any[]> = new Map();
  // Computed filtered categories - updated only when necessary
  filteredCategories: any[] = [];

  // Helper method to load discounted categories for SALES tab asynchronously
  private async loadDiscountedCategoriesForSales(): Promise<void> {
    try {
      const discountedCategories = await this.getDiscountedCategories();
      this.salesTab.discountedSubcategories = discountedCategories;
      this.filteredCategories = discountedCategories;
      this.updateDesktopSections();
      this.cdr.markForCheck();
    } catch (error) {
      // Silent error handling
    }
  }

  // Helper method to load new categories for NEW tab asynchronously
  private async loadNewCategoriesForNew(): Promise<void> {
    const requestId = ++this.newProductsLoadId;
    try {
      const newCategories = await this.getNewCategories(requestId);
      if (requestId !== this.newProductsLoadId) {
        return;
      }
      this.newTab.newSubcategories = newCategories;
      this.filteredCategories = newCategories;
      this.updateDesktopSections();
      this.cdr.markForCheck();
    } catch (error) {
      // Silent error handling
    }
  }

  // Helper method to find a category by ID (including subcategories)
  private findCategoryById(categoryId: number): any {
    // First, check main categories
    for (const category of this.categories) {
      if (category.id === categoryId) {
        return category;
      }

      // Check subcategories
      if (category.children && category.children.length > 0) {
        for (const child of category.children) {
          if (child.id === categoryId) {
            return {
              ...child,
              parentLabel: category.label || category.name,
              parentId: category.id
            };
          }
        }
      }
    }

    return null;
  }

  // Update filtered categories when category selection changes
  private updateFilteredCategories(): void {
    const categoryToFilter = this.selectedSubTab || this.currentCategory || this.hoveredCategory;

    if (!categoryToFilter) {
      // Show all categories (temporarily removed show filter)
      this.filteredCategories = this.categories;
      return;
    }

    // Special handling for SALE tab
    if (categoryToFilter.id === -1 && categoryToFilter.name === 'SALE') {
      // For SALE tab, return the discounted subcategories stored in the salesTab
      if (this.salesTab.discountedSubcategories && this.salesTab.discountedSubcategories.length > 0) {
        this.filteredCategories = this.salesTab.discountedSubcategories;
      } else {
        // Fallback: get fresh discounted categories asynchronously
        this.loadDiscountedCategoriesForSales();
        this.filteredCategories = [];
      }
      return;
    }

    // Special handling for NEW tab
    if (categoryToFilter.id === -2 && categoryToFilter.name === 'NEW') {
      // For NEW tab, return the new subcategories stored in the newTab
      if (this.newTab.newSubcategories && this.newTab.newSubcategories.length > 0) {
        this.filteredCategories = this.newTab.newSubcategories;
      } else {
        // Fallback: get fresh new categories asynchronously
        this.loadNewCategoriesForNew();
        this.filteredCategories = [];
      }
      return;
    }

    // If no specific category IDs are defined, show all categories
    if (!categoryToFilter.categoryIds || categoryToFilter.categoryIds.length === 0) {
      this.filteredCategories = this.categories;
      return;
    }

    // Create cache key
    const cacheKey = `${categoryToFilter.id}_${categoryToFilter.categoryIds.join('_')}`;

    // Check cache first
    if (this.categoriesCache.has(cacheKey)) {
      this.filteredCategories = this.categoriesCache.get(cacheKey)!;
      return;
    }

    // Convert categoryIds to Set for O(1) lookup instead of O(n) includes()
    const categoryIdsSet = new Set(categoryToFilter.categoryIds);

    // Filter categories based on the selected tab's categoryIds
    const filtered = this.categories.filter(category =>
      categoryIdsSet.has(category.id)
    );

    // Sort the filtered categories according to the exact order in categoryIds
    // This ensures categories appear in the order they were selected in the admin panel
    const orderedFiltered = categoryToFilter.categoryIds
      .map((id: number) => filtered.find(category => category.id === id))
      .filter((category: any) => category !== undefined);

    // Cache the result
    this.categoriesCache.set(cacheKey, orderedFiltered);
    this.filteredCategories = orderedFiltered;
  }

  // Calculate total product count including all subcategories recursively
  getTotalProductCount(category: any): number {
    if (!category) return 0;

    // For special tabs (SALE/NEW), show their specific counts
    const currentTab = this.selectedSubTab || this.currentCategory || this.hoveredCategory;

    if (currentTab?.name === 'SALE') {
      // For hierarchical structure, use totalDiscountedProductCount if available
      if (category.totalDiscountedProductCount !== undefined && category.totalDiscountedProductCount !== null) {
        return category.totalDiscountedProductCount;
      }
      // Fallback to individual discountedProductCount
      if (category.discountedProductCount !== undefined && category.discountedProductCount !== null) {
        return category.discountedProductCount;
      }
    }

    if (currentTab?.name === 'NEW') {
      // For hierarchical structure, use totalNewProductCount if available
      if (category.totalNewProductCount !== undefined && category.totalNewProductCount !== null) {
        return category.totalNewProductCount;
      }
      // Fallback to individual newProductCount
      if (category.newProductCount !== undefined && category.newProductCount !== null) {
        return category.newProductCount;
      }
    }

    // Default: regular product count
    let totalCount = category.products_count || 0;

    // Add counts from children recursively
    if (category.children && category.children.length > 0) {
      totalCount += category.children.reduce((sum: number, child: any) => {
        return sum + this.getTotalProductCount(child);
      }, 0);
    }

    return totalCount;
  }

  // Add SALES tab to titles array
  private async addSalesTab(): Promise<void> {
    // Check if SALE tab already exists to prevent duplicates
    const salesTabExists = this.titles.find(tab => tab.id === -1 && tab.name === 'SALE');
    if (salesTabExists) {
      return;
    }

    // Always add the SALES tab first, then populate it
    this.titles.push(this.salesTab);

    try {
      // Get subcategories with discounted products
      const discountedSubcategories = await this.getDiscountedCategories();

      // Store the discounted subcategories directly in the sales tab for display
      this.salesTab.discountedSubcategories = discountedSubcategories;

      // Store the subcategory IDs for reference
      this.salesTab.categoryIds = discountedSubcategories.map(sub => sub.id);

    } catch (error) {
      // Keep the SALES tab but mark it as empty
      this.salesTab.discountedSubcategories = [];
      this.salesTab.categoryIds = [];
    }

    // Trigger change detection after adding the tab
    this.cdr.markForCheck();
  }

  // Update SALES tab after categories are loaded
  private async updateSalesTab(): Promise<void> {
    // Remove existing SALE tab if present
    const salesTabIndex = this.titles.findIndex(tab => tab.id === -1 && tab.name === 'SALE');
    if (salesTabIndex !== -1) {
      this.titles.splice(salesTabIndex, 1);
    }

    // Add updated SALES tab
    await this.addSalesTab();
    this.updateDesktopSections();
    if (this.menuSelection === 'SALE') {
      this.applyMenuSelection();
    }

    // Trigger change detection after updating titles
    this.cdr.markForCheck();
  }

  // Add NEW tab to titles array
  private async addNewTab(requestId: number): Promise<void> {
    // Check if NEW tab already exists to prevent duplicates
    const newTabExists = this.titles.find(tab => tab.id === -2 && tab.name === 'NEW');
    if (newTabExists) {
      return;
    }

    // Always add the NEW tab first, then populate it
    this.titles.push(this.newTab);

    try {
      // Get subcategories with new products
      const newSubcategories = await this.getNewCategories(requestId);
      if (requestId !== this.newProductsLoadId) {
        return;
      }

      // Store the new subcategories directly in the new tab for display
      this.newTab.newSubcategories = newSubcategories;

      // Store the subcategory IDs for reference
      this.newTab.categoryIds = newSubcategories.map(sub => sub.id);

    } catch (error) {
      if (requestId !== this.newProductsLoadId) {
        return;
      }
      // Keep the NEW tab but mark it as empty
      this.newTab.newSubcategories = [];
      this.newTab.categoryIds = [];
    }

    // Trigger change detection after adding the tab
    this.cdr.markForCheck();
  }

  // Update NEW tab after categories are loaded
  private async updateNewTab(): Promise<void> {
    const requestId = ++this.newProductsLoadId;
    // Remove existing NEW tab if present
    const newTabIndex = this.titles.findIndex(tab => tab.id === -2 && tab.name === 'NEW');
    if (newTabIndex !== -1) {
      this.titles.splice(newTabIndex, 1);
    }

    // Add updated NEW tab
    await this.addNewTab(requestId);
    if (requestId !== this.newProductsLoadId) {
      return;
    }
    this.updateDesktopSections();
    if (this.menuSelection === 'NEW') {
      this.applyMenuSelection();
    }

    // Trigger change detection after updating titles
    this.cdr.markForCheck();
  }

  // Find categories that contain subcategories with discounted products
  private findCategoriesWithDiscounts(): number[] {
    const categoriesWithDiscounts: number[] = [];

    this.categories.forEach(category => {
      // Check if this category has children with discounted products
      if (this.categoryHasDiscountedProducts(category)) {
        categoriesWithDiscounts.push(category.id);
      }
    });

    // Also start async API check in the background for better accuracy
    this.asyncCheckForDiscountedProducts();

    return categoriesWithDiscounts;
  }

  // Asynchronously check for discounted products using API calls
  private async asyncCheckForDiscountedProducts(): Promise<void> {
    const categoriesWithDiscounts: number[] = [];

    for (const category of this.categories) {
      const hasDiscounts = await this.checkCategoryForDiscountedProducts(category.id);
      if (hasDiscounts) {
        categoriesWithDiscounts.push(category.id);
      }

      // Also check subcategories
      if (category.children && category.children.length > 0) {
        for (const child of category.children) {
          const childHasDiscounts = await this.checkCategoryForDiscountedProducts(child.id);
          if (childHasDiscounts && !categoriesWithDiscounts.includes(category.id)) {
            categoriesWithDiscounts.push(category.id);
            break; // No need to check other children of this category
          }
        }
      }
    }

    // Update SALES tab if we found different results
    const currentCategoryIds = this.salesTab.categoryIds || [];
    if (categoriesWithDiscounts.length !== currentCategoryIds.length ||
      !categoriesWithDiscounts.every(id => currentCategoryIds.includes(id))) {
      this.salesTab.categoryIds = categoriesWithDiscounts;
      this.cdr.markForCheck();
    }
  }

  // Check if a category or its children have discounted products
  private categoryHasDiscountedProducts(category: any): boolean {
    // Check if category has children (subcategories)
    if (category.children && category.children.length > 0) {
      const hasDiscountedChildren = category.children.some((child: any) => {
        // Check if subcategory has discounted products
        return this.subcategoryHasDiscountedProducts(child) ||
          this.categoryHasDiscountedProducts(child); // Recursive check for nested categories
      });

      if (hasDiscountedChildren) {
        return true;
      }
    }

    // If no children, check if this category itself has discounted products
    const categoryHasDiscounts = this.subcategoryHasDiscountedProducts(category);

    return categoryHasDiscounts;
  }

  // Check if a subcategory has products with discounts
  private subcategoryHasDiscountedProducts(subcategory: any): boolean {
    // Check if subcategory has products with actual discount rates
    if (subcategory.products && subcategory.products.length > 0) {
      const hasDiscountedProducts = subcategory.products.some((product: any) => {
        const hasDiscount = product.discountRate && product.discountRate > 0;
        return hasDiscount;
      });
      if (hasDiscountedProducts) {
        return true;
      }
    }

    // Check if subcategory has discount flags
    if (subcategory.hasDiscount || subcategory.onSale) {
      return true;
    }

    // Check if any product_count indicates discounted items
    if (subcategory.discounted_products_count && subcategory.discounted_products_count > 0) {
      return true;
    }

    return false;
  }

  // Verify that a category actually has discounted products by making a fresh API call
  // Simplified verification - just return true to avoid over-filtering
  private async verifyDiscountedProductsInCategory(categoryId: number): Promise<boolean> {
    // For now, we'll trust that if a category was found in the discounted products list,
    // it should be displayed. This prevents over-filtering that might hide valid categories.
    return true;
  }

  // Get categories that contain discounted products for SALES tab
  private async getDiscountedCategories(): Promise<any[]> {
    try {
      // Try to get products with discount filter
      const discountQuery = {
        discount: true
      };

      let discountResponse;
      try {
        discountResponse = await this.storageService.getProducts(1, 50, '', discountQuery).pipe(
          timeout(10000),
          take(1)
        ).toPromise();
      } catch (apiError) {
        discountResponse = null;
      }

      if (discountResponse && discountResponse.data && discountResponse.data.length > 0) {

        // Debug: Check what discount-related fields these products actually have
        discountResponse.data.slice(0, 3).forEach((product, index) => {
        });

        // Filter products that actually have discount indicators
        const actuallyDiscountedProducts = discountResponse.data.filter(product => {
          // Check for discountRate > 0 (handle both number and string types)
          const discountRate = parseFloat(product.discountRate);
          if (!isNaN(discountRate) && discountRate > 0) {
            return true;
          }

          // Check for originalPrice > priceHT (handle string prices)
          const originalPrice = parseFloat(product.originalPrice);
          const priceHT = parseFloat(product.priceHT);
          if (!isNaN(originalPrice) && !isNaN(priceHT) && originalPrice > priceHT) {
            return true;
          }

          // Check for isOnSale flag
          if (product.isOnSale === true || product.isOnSale === 1) {
            return true;
          }

          // Check for discount field
          if (product.discount && (product.discount === true || product.discount === 1)) {
            return true;
          }

          return false;
        });


        if (actuallyDiscountedProducts.length === 0) {
          return [];
        }

        // Extract category IDs from actually discounted products
        const discountedCategoryIds = new Set<number>();
        const categoryDiscountCounts = new Map<number, number>();

        actuallyDiscountedProducts.forEach(product => {
          const categoryId = product.category?.id || product.category;

          if (categoryId && typeof categoryId === 'number') {
            discountedCategoryIds.add(categoryId);
            categoryDiscountCounts.set(categoryId, (categoryDiscountCounts.get(categoryId) || 0) + 1);
          }
        });

        // Group subcategories by their parent category
        const parentCategoriesMap = new Map<number, any>();

        for (const categoryId of discountedCategoryIds) {
          for (const mainCategory of this.categories) {
            if (mainCategory.children && mainCategory.children.length > 0) {
              for (const subcategory of mainCategory.children) {
                if (subcategory.id === categoryId) {
                  const parentId = mainCategory.id;

                  // Get or create parent category
                  if (!parentCategoriesMap.has(parentId)) {
                    parentCategoriesMap.set(parentId, {
                      ...mainCategory,
                      children: [],
                      totalDiscountedProductCount: 0
                    });
                  }

                  // Add subcategory to parent
                  const parentCategory = parentCategoriesMap.get(parentId);
                  const subcategoryWithContext = {
                    ...subcategory,
                    routerLink: `/products/category/${subcategory.id}`,
                    label: subcategory.label || subcategory.name,
                    name: subcategory.name || subcategory.label,
                    discountedProductCount: categoryDiscountCounts.get(categoryId) || 0
                  };
                  parentCategory.children.push(subcategoryWithContext);
                  parentCategory.totalDiscountedProductCount += categoryDiscountCounts.get(categoryId) || 0;
                  break;
                }
              }
            }
            else if (mainCategory.id === categoryId) {
              // This is a main category without children
              const categoryWithContext = {
                ...mainCategory,
                routerLink: `/products/category/${mainCategory.id}`,
                label: mainCategory.label || mainCategory.name,
                name: mainCategory.name || mainCategory.label,
                discountedProductCount: categoryDiscountCounts.get(categoryId) || 0,
                children: [],
                totalDiscountedProductCount: categoryDiscountCounts.get(categoryId) || 0
              };
              parentCategoriesMap.set(categoryId, categoryWithContext);
            }
          }
        }

        // Convert map to array
        const discountedSubcategories = Array.from(parentCategoriesMap.values());

        if (discountedSubcategories.length > 0) {
          return discountedSubcategories;
        }
      }

      // Fallback: Get all products and filter client-side for discounted products
      let allProductsResponse;
      try {
        allProductsResponse = await this.storageService.getProducts(1, 100, '', {}).pipe(
          timeout(10000),
          take(1)
        ).toPromise();
      } catch (fallbackError) {
        allProductsResponse = null;
      }

      if (allProductsResponse && allProductsResponse.data && allProductsResponse.data.length > 0) {
        // Filter for discounted products using strict criteria
        const discountedProducts = allProductsResponse.data.filter(product => {
          // Only include products with explicit discount indicators

          // Check for discountRate > 0 (handle both number and string types)
          const discountRate = parseFloat(product.discountRate);
          if (!isNaN(discountRate) && discountRate > 0) {
            return true;
          }

          // Check for originalPrice > priceHT (indicating a discount)
          const originalPrice = parseFloat(product.originalPrice);
          const priceHT = parseFloat(product.priceHT);
          if (!isNaN(originalPrice) && !isNaN(priceHT) && originalPrice > priceHT) {
            return true;
          }

          // Check for explicit isOnSale flag
          if (product.isOnSale === true || product.isOnSale === 1) {
            return true;
          }

          // Check for discount field
          if (product.discount && (product.discount === true || product.discount === 1)) {
            return true;
          }

          return false;
        });


        // If no discounted products found, return empty array
        if (discountedProducts.length === 0) {
          return [];
        }

        if (discountedProducts.length > 0) {
          const discountedCategoryIds = new Set<number>();
          const categoryDiscountCounts = new Map<number, number>();

          discountedProducts.forEach(product => {
            const categoryId = product.category?.id || product.category;

            if (categoryId && typeof categoryId === 'number') {
              discountedCategoryIds.add(categoryId);
              categoryDiscountCounts.set(categoryId, (categoryDiscountCounts.get(categoryId) || 0) + 1);
            }
          });

          const discountedSubcategories: any[] = [];

          for (const categoryId of discountedCategoryIds) {
            for (const mainCategory of this.categories) {
              if (mainCategory.children && mainCategory.children.length > 0) {
                for (const subcategory of mainCategory.children) {
                  if (subcategory.id === categoryId) {
                    const subcategoryWithContext = {
                      ...subcategory,
                      parentLabel: mainCategory.label || mainCategory.name,
                      parentId: mainCategory.id,
                      routerLink: `/products/category/${subcategory.id}`,
                      label: subcategory.label || subcategory.name,
                      name: subcategory.name || subcategory.label,
                      discountedProductCount: categoryDiscountCounts.get(categoryId) || 0
                    };
                    discountedSubcategories.push(subcategoryWithContext);
                    break;
                  }
                }
              }
              else if (mainCategory.id === categoryId) {
                const categoryWithContext = {
                  ...mainCategory,
                  routerLink: `/products/category/${mainCategory.id}`,
                  label: mainCategory.label || mainCategory.name,
                  name: mainCategory.name || mainCategory.label,
                  discountedProductCount: categoryDiscountCounts.get(categoryId) || 0
                };
                discountedSubcategories.push(categoryWithContext);
              }
            }
          }

          return discountedSubcategories;
        }
      }

      // No fallback - only show categories that actually have discounted products
      return [];

    } catch (error) {
      return [];
    }
  }

  // Get categories that contain new products for NEW tab
  private async getNewCategories(requestId: number): Promise<any[]> {
    if (requestId !== this.newProductsLoadId) {
      return [];
    }
    this.isLoadingNewProducts = true;
    this.newProductsError = false;
    let productRequestSucceeded = false;
    let unexpectedFailure = false;
    try {

      // Try to get products with isNew filter
      const newProductsQuery = {
        isNew: 'true'
      };

      let newProductsResponse;
      try {
        newProductsResponse = await this.storageService.getProducts(1, 50, '', newProductsQuery, true, true).pipe(
          timeout(10000),
          take(1)
        ).toPromise();
      } catch (apiError) {
        newProductsResponse = null;
      }
      if (requestId !== this.newProductsLoadId) {
        return [];
      }
      productRequestSucceeded = Array.isArray(newProductsResponse?.data);

      if (newProductsResponse && newProductsResponse.data && newProductsResponse.data.length > 0) {
        this.setNewProducts(newProductsResponse.data);
        // Extract category IDs and validate they actually have new products
        const newCategoryIds = new Set<number>();
        const categoryNewCounts = new Map<number, number>();

        newProductsResponse.data.forEach(product => {
          const categoryId = product.category?.id || product.category;

          if (categoryId && typeof categoryId === 'number' && product.isNew === true) {
            newCategoryIds.add(categoryId);
            categoryNewCounts.set(categoryId, (categoryNewCounts.get(categoryId) || 0) + 1);
          }
        });

        // Group subcategories by their parent category
        const parentCategoriesMap = new Map<number, any>();

        for (const categoryId of newCategoryIds) {
          for (const mainCategory of this.categories) {
            if (mainCategory.children && mainCategory.children.length > 0) {
              for (const subcategory of mainCategory.children) {
                if (subcategory.id === categoryId) {
                  const parentId = mainCategory.id;

                  // Get or create parent category
                  if (!parentCategoriesMap.has(parentId)) {
                    parentCategoriesMap.set(parentId, {
                      ...mainCategory,
                      children: [],
                      totalNewProductCount: 0
                    });
                  }

                  // Add subcategory to parent
                  const parentCategory = parentCategoriesMap.get(parentId);
                  const subcategoryWithContext = {
                    ...subcategory,
                    routerLink: `/products/category/${subcategory.id}`,
                    label: subcategory.label || subcategory.name,
                    name: subcategory.name || subcategory.label,
                    newProductCount: categoryNewCounts.get(categoryId) || 0
                  };
                  parentCategory.children.push(subcategoryWithContext);
                  parentCategory.totalNewProductCount += categoryNewCounts.get(categoryId) || 0;
                  break;
                }
              }
            }
            else if (mainCategory.id === categoryId) {
              // This is a main category without children
              const categoryWithContext = {
                ...mainCategory,
                routerLink: `/products/category/${mainCategory.id}`,
                label: mainCategory.label || mainCategory.name,
                name: mainCategory.name || mainCategory.label,
                newProductCount: categoryNewCounts.get(categoryId) || 0,
                children: [],
                totalNewProductCount: categoryNewCounts.get(categoryId) || 0
              };
              parentCategoriesMap.set(categoryId, categoryWithContext);
            }
          }
        }

        // Convert map to array
        const newSubcategories = Array.from(parentCategoriesMap.values());

        if (newSubcategories.length > 0) {
          return newSubcategories;
        }
      }

      // Fallback: Get all products and filter client-side
      let allProductsResponse;
      try {
        allProductsResponse = await this.storageService.getProducts(1, 100, '', { sort: 'newest' }, true, true).pipe(
          timeout(10000),
          take(1)
        ).toPromise();
      } catch (fallbackError) {
        allProductsResponse = null;
      }
      if (requestId !== this.newProductsLoadId) {
        return [];
      }
      productRequestSucceeded = productRequestSucceeded || Array.isArray(allProductsResponse?.data);

      if (allProductsResponse && allProductsResponse.data && allProductsResponse.data.length > 0) {
        // Filter for new products using multiple criteria
        const newProducts = allProductsResponse.data.filter(product => {
          if (product.isNew === true || product.isNew === 1) {
            return true;
          }

          const createdAt = product.created_at || product.createdAt || product.created_at_date;
          if (createdAt) {
            const createdDate = new Date(createdAt);
            if (!isNaN(createdDate.getTime())) {
              const now = new Date();
              const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000;
              return now.getTime() - createdDate.getTime() <= THIRTY_DAYS_MS;
            }
          }

          return false;
        });
        this.setNewProducts(newProducts.length > 0 ? newProducts : allProductsResponse.data);

        if (newProducts.length > 0) {
          const newCategoryIds = new Set<number>();
          const categoryNewCounts = new Map<number, number>();

          newProducts.forEach(product => {
            const categoryId = product.category?.id || product.category;

            if (categoryId && typeof categoryId === 'number') {
              newCategoryIds.add(categoryId);
              categoryNewCounts.set(categoryId, (categoryNewCounts.get(categoryId) || 0) + 1);
            }
          });

          // Group subcategories by their parent category
          const parentCategoriesMap = new Map<number, any>();

          for (const categoryId of newCategoryIds) {
            for (const mainCategory of this.categories) {
              if (mainCategory.children && mainCategory.children.length > 0) {
                for (const subcategory of mainCategory.children) {
                  if (subcategory.id === categoryId) {
                    const parentId = mainCategory.id;

                    // Get or create parent category
                    if (!parentCategoriesMap.has(parentId)) {
                      parentCategoriesMap.set(parentId, {
                        ...mainCategory,
                        children: [],
                        totalNewProductCount: 0
                      });
                    }

                    // Add subcategory to parent
                    const parentCategory = parentCategoriesMap.get(parentId);
                    const subcategoryWithContext = {
                      ...subcategory,
                      routerLink: `/products/category/${subcategory.id}`,
                      label: subcategory.label || subcategory.name,
                      name: subcategory.name || subcategory.label,
                      newProductCount: categoryNewCounts.get(categoryId) || 0
                    };
                    parentCategory.children.push(subcategoryWithContext);
                    parentCategory.totalNewProductCount += categoryNewCounts.get(categoryId) || 0;
                    break;
                  }
                }
              }
              else if (mainCategory.id === categoryId) {
                // This is a main category without children
                const categoryWithContext = {
                  ...mainCategory,
                  routerLink: `/products/category/${mainCategory.id}`,
                  label: mainCategory.label || mainCategory.name,
                  name: mainCategory.name || mainCategory.label,
                  newProductCount: categoryNewCounts.get(categoryId) || 0,
                  children: [],
                  totalNewProductCount: categoryNewCounts.get(categoryId) || 0
                };
                parentCategoriesMap.set(categoryId, categoryWithContext);
              }
            }
          }

          // Convert map to array
          const newSubcategories = Array.from(parentCategoriesMap.values());

          return newSubcategories;
        }
      }

      // Final fallback: Try to get recent products (last 30 days) and show their categories
      let recentProductsResponse;
      try {
        // Get products sorted by creation date (newest first)
        recentProductsResponse = await this.storageService.getProducts(1, 50, '', {
          sort: 'newest'
        }, true, true).pipe(
          timeout(10000),
          take(1)
        ).toPromise();
      } catch (recentError) {
        recentProductsResponse = null;
      }
      if (requestId !== this.newProductsLoadId) {
        return [];
      }
      productRequestSucceeded = productRequestSucceeded || Array.isArray(recentProductsResponse?.data);

      if (recentProductsResponse && recentProductsResponse.data && recentProductsResponse.data.length > 0) {
        // Filter for products created within last 30 days
        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

        const recentProducts = recentProductsResponse.data.filter(product => {
          const createdAt = product.created_at || product.createdAt || product.created_at_date;
          if (createdAt) {
            const createdDate = new Date(createdAt);
            return !isNaN(createdDate.getTime()) && createdDate >= thirtyDaysAgo;
          }
          return false;
        });
        if (recentProducts.length > 0) {
          this.setNewProducts(recentProducts);
        }

        if (recentProducts.length > 0) {
          // Get unique category IDs from recent products
          const recentCategoryIds = new Set<number>();
          const categoryRecentCounts = new Map<number, number>();

          recentProducts.forEach(product => {
            const categoryId = product.category?.id || product.category;

            if (categoryId && typeof categoryId === 'number') {
              recentCategoryIds.add(categoryId);
              categoryRecentCounts.set(categoryId, (categoryRecentCounts.get(categoryId) || 0) + 1);
            }
          });

          const recentSubcategories: any[] = [];

          for (const categoryId of recentCategoryIds) {
            for (const mainCategory of this.categories) {
              if (mainCategory.children && mainCategory.children.length > 0) {
                for (const subcategory of mainCategory.children) {
                  if (subcategory.id === categoryId) {
                    const subcategoryWithContext = {
                      ...subcategory,
                      parentLabel: mainCategory.label || mainCategory.name,
                      parentId: mainCategory.id,
                      routerLink: `/products/category/${subcategory.id}`,
                      label: subcategory.label || subcategory.name,
                      name: subcategory.name || subcategory.label,
                      newProductCount: categoryRecentCounts.get(categoryId) || 0
                    };
                    recentSubcategories.push(subcategoryWithContext);
                    break;
                  }
                }
              }
              else if (mainCategory.id === categoryId) {
                const categoryWithContext = {
                  ...mainCategory,
                  routerLink: `/products/category/${mainCategory.id}`,
                  label: mainCategory.label || mainCategory.name,
                  name: mainCategory.name || mainCategory.label,
                  newProductCount: categoryRecentCounts.get(categoryId) || 0
                };
                recentSubcategories.push(categoryWithContext);
              }
            }
          }

          if (recentSubcategories.length > 0) {
            return recentSubcategories;
          }
        }
      }

      // Ultimate fallback: Show some categories if no recent products found
      const fallbackCategories: any[] = [];

      for (const mainCategory of this.categories.slice(0, 3)) {
        if (mainCategory.children && mainCategory.children.length > 0) {
          const subcategories = mainCategory.children.slice(0, 2);
          for (const subcategory of subcategories) {
            const subcategoryWithContext = {
              ...subcategory,
              parentLabel: mainCategory.label || mainCategory.name,
              parentId: mainCategory.id,
              routerLink: `/products/category/${subcategory.id}`,
              label: subcategory.label || subcategory.name,
              name: subcategory.name || subcategory.label,
              newProductCount: null // Hide product count for fallback
            };
            fallbackCategories.push(subcategoryWithContext);
          }
        } else {
          const categoryWithContext = {
            ...mainCategory,
            routerLink: `/products/category/${mainCategory.id}`,
            label: mainCategory.label || mainCategory.name,
            name: mainCategory.name || mainCategory.label,
            newProductCount: null // Hide product count for fallback
          };
          fallbackCategories.push(categoryWithContext);
        }
      }

      return fallbackCategories;

    } catch (error) {
      unexpectedFailure = true;
      return [];
    } finally {
      if (requestId === this.newProductsLoadId) {
        if (!this.newProducts.length) {
          this.newProductsError = unexpectedFailure || !productRequestSucceeded;
        }
        this.isLoadingNewProducts = false;
        this.cdr.markForCheck();
      }
    }
  }

  private setNewProducts(products: any[]): void {
    this.newProducts = [...products]
      .filter(product => Boolean(product))
      .sort((first, second) => this.getProductCreationTime(second) - this.getProductCreationTime(first))
      .slice(0, 12);
    this.newProductCards = this.newProducts.map(product => ({
      product,
      image: this.getNewProductImage(product),
      category: this.getNewProductCategory(product),
      reference: this.getNewProductReference(product),
      producer: product?.producer?.label || product?.producer?.name || product?.producer || '',
      badge: this.isNewProduct(product) ? 'NEW' : 'LATEST',
      availability: this.getNewProductAvailability(product),
      pricing: this.getNewProductPricing(product)
    }));
    requestAnimationFrame(() => this.updateNewProductsScrollState());
    this.newProductsError = false;
  }

  private isRecentlyCreated(product: any): boolean {
    const createdAt = this.getProductCreationTime(product);
    const age = Date.now() - createdAt;
    return createdAt > 0 && age >= 0 && age <= 30 * 24 * 60 * 60 * 1000;
  }

  isNewProduct(product: any): boolean {
    return product?.isNew === true || product?.isNew === 1 || product?.isNew === 'true' ||
      this.isRecentlyCreated(product);
  }

  private getProductCreationTime(product: any): number {
    const createdAt = product?.created_at || product?.createdAt || product?.created_at_date;
    const timestamp = createdAt ? new Date(createdAt).getTime() : 0;
    return Number.isNaN(timestamp) ? 0 : timestamp;
  }

  getNewProductImage(product: any): string {
    const documentImage = product?.documents?.find((document: any) =>
      typeof document?.url === 'string' &&
      /\.(avif|gif|jpe?g|png|svg|webp)(?:[?#].*)?$/i.test(document.url)
    )?.url;
    const colorImage = product?.colors
      ?.flatMap((color: any) => color?.documents || [])
      .find((document: any) =>
        typeof document?.url === 'string' &&
        /\.(avif|gif|jpe?g|png|svg|webp)(?:[?#].*)?$/i.test(document.url)
      )?.url;

    const imageUrl = product?.thumbnail || documentImage || colorImage || product?.image ||
      product?.images?.find((image: any) => typeof image === 'string' && image.trim()) ||
      '/images/placeholder-product.svg';
    return this.imageOptimizationService.getResponsiveImageUrl(imageUrl, 640, 75);
  }

  getNewProductCategory(product: any): string {
    const additionalCategory = product?.additionalCategories?.find(
      (category: any) => category?.label || category?.name
    );
    return product?.category?.label || product?.category?.name || product?.categoryName ||
      additionalCategory?.label || additionalCategory?.name || '';
  }

  getNewProductReference(product: any): string {
    return product?.reference || product?.code || '';
  }

  getNewProductAvailability(product: any): { label: string; className: string } | null {
    if (product?.availability === null || product?.availability === undefined || product?.availability === '') {
      return null;
    }
    const availability = Number(product?.availability);
    const statuses: Record<number, { label: string; className: string }> = {
      0: { label: 'Dostępny', className: 'in-stock' },
      1: { label: 'Niedostępny', className: 'out-of-stock' },
      2: { label: 'Wkrótce dostępny', className: 'arriving-soon' },
      3: { label: 'Na zamówienie', className: 'made-to-order' },
      4: { label: 'W magazynie producenta', className: 'factory-stock' }
    };

    return Number.isInteger(availability) ? statuses[availability] || null : null;
  }

  getNewProductPricing(product: any): { price: number | null; originalPrice: number | null; discount: number } {
    const currentPrice = Number(product?.priceHT ?? product?.price);
    if (!Number.isFinite(currentPrice) || currentPrice <= 0) {
      return { price: null, originalPrice: null, discount: 0 };
    }

    const discount = Number(product?.discountRate ?? product?.discount ?? 0);
    const hasDiscount = Number.isFinite(discount) && discount > 0 && discount < 100;
    const suppliedOriginalPrice = Number(product?.originalPrice ?? product?.priceHTOriginal);
    const originalPrice = suppliedOriginalPrice > currentPrice
      ? suppliedOriginalPrice
      : hasDiscount ? currentPrice : null;
    const price = hasDiscount && !(suppliedOriginalPrice > currentPrice)
      ? currentPrice * (1 - discount / 100)
      : currentPrice;
    const discountPercent = originalPrice
      ? Math.round(discount || (1 - price / originalPrice) * 100)
      : 0;

    return { price, originalPrice, discount: discountPercent };
  }

  onNewProductImageError(event: Event): void {
    if (event.target instanceof HTMLImageElement) {
      event.target.src = '/images/placeholder-product.svg';
    }
  }

  scrollNewProducts(direction: -1 | 1): void {
    const track = this.newProductsTrack?.nativeElement;
    if (track) {
      const card = track.querySelector<HTMLElement>('.desktop-new-product-card');
      const gap = Number.parseFloat(getComputedStyle(track).columnGap) || 0;
      const scrollDistance = card ? (card.offsetWidth + gap) * 2 : track.clientWidth * 0.8;
      track.scrollTo({
        left: track.scrollLeft + direction * scrollDistance,
        behavior: 'smooth'
      });
    }
  }

  openNewProductPreview(product: any, event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.selectedNewProduct = product;
    this.showNewProductPreview = true;
  }

  closeNewProductPreview(): void {
    this.showNewProductPreview = false;
    this.selectedNewProduct = null;
  }

  requestNewProductQuoteFor(product: any, event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    if (!product?.id) {
      return;
    }

    this.onLinkClick();
    void this.router.navigate(['/products', product.id]);
  }

  updateNewProductsScrollState(): void {
    const track = this.newProductsTrack?.nativeElement;
    if (!track) {
      return;
    }

    const canScrollLeft = track.scrollLeft > 1;
    const maxScroll = Math.max(0, track.scrollWidth - track.clientWidth);
    const canScrollRight = track.scrollLeft < maxScroll - 1;
    const firstCard = track.querySelector<HTMLElement>('.desktop-new-product-card');
    const gap = Number.parseFloat(getComputedStyle(track).columnGap) || 0;
    const pageStep = firstCard ? (firstCard.offsetWidth + gap) * 2 : track.clientWidth;
    const pageCount = maxScroll > 1 ? Math.ceil(maxScroll / Math.max(pageStep, 1)) + 1 : 1;
    const activePage = maxScroll > 1
      ? Math.min(pageCount - 1, Math.round((track.scrollLeft / maxScroll) * (pageCount - 1)))
      : 0;
    if (
      canScrollLeft !== this.newProductsCanScrollLeft ||
      canScrollRight !== this.newProductsCanScrollRight ||
      pageCount !== this.newProductsPageCount ||
      activePage !== this.newProductsActivePage
    ) {
      this.newProductsCanScrollLeft = canScrollLeft;
      this.newProductsCanScrollRight = canScrollRight;
      this.newProductsPageCount = pageCount;
      this.newProductsActivePage = activePage;
      this.cdr.markForCheck();
    }
  }

  goToNewProductsPage(page: number): void {
    const track = this.newProductsTrack?.nativeElement;
    if (!track || page < 0 || page >= this.newProductsPageCount) {
      return;
    }

    const maxScroll = Math.max(0, track.scrollWidth - track.clientWidth);
    const pageProgress = this.newProductsPageCount > 1
      ? page / (this.newProductsPageCount - 1)
      : 0;
    track.scrollTo({ left: maxScroll * pageProgress, behavior: 'smooth' });
  }

  trackByNewProductCard(index: number, card: NewProductCard): number | string {
    return card.product?.id ?? index;
  }

  // Simplified verification - just return true to avoid over-filtering
  private async verifyNewProductsInCategory(categoryId: number): Promise<boolean> {
    // For now, we'll trust that if a category was found in the new products list,
    // it should be displayed. This prevents over-filtering that might hide valid categories.
    return true;
  }

  // Verify that a category actually has discounted products by making a fresh API call
  private async checkCategoryForDiscountedProducts(categoryId: number): Promise<boolean> {
    try {
      // Approach 1: Try MongoDB-style query with category filter
      try {
        const mongoQuery = {
          category: categoryId,
          discountRate: { $gt: 0 }
        };

        const mongoResponse = await this.storageService.getProducts(1, 1, '', mongoQuery).toPromise();
        if (mongoResponse && mongoResponse.data && mongoResponse.data.length > 0) {
          return true;
        }
      } catch (mongoError) {
        // Continue to next approach
      }

      // Approach 2: Get all products in category and filter client-side
      try {
        const allProductsQuery = { category: categoryId };
        const allProductsResponse = await this.storageService.getProducts(1, 20, '', allProductsQuery).toPromise();

        if (allProductsResponse && allProductsResponse.data && allProductsResponse.data.length > 0) {
          // Filter for products with actual discounts
          const discountedProducts = allProductsResponse.data.filter(product => (product.discountRate || 0) > 0);

          if (discountedProducts.length > 0) {
            return true;
          }
        }
      } catch (allError) {
        // Continue to next approach
      }

      // Approach 3: Try different query formats
      try {
        // Try with array format
        const arrayQuery = {
          category: [categoryId],
          discountRate: { $gt: 0 }
        };

        const arrayResponse = await this.storageService.getProducts(1, 1, '', arrayQuery).toPromise();
        if (arrayResponse && arrayResponse.data && arrayResponse.data.length > 0) {
          return true;
        }

        // Try with sub_category field
        const subCategoryQuery = {
          sub_category: [categoryId],
          discountRate: { $gt: 0 }
        };

        const subCategoryResponse = await this.storageService.getProducts(1, 1, '', subCategoryQuery).toPromise();
        if (subCategoryResponse && subCategoryResponse.data && subCategoryResponse.data.length > 0) {
          return true;
        }
      } catch (formatError) {
        // Continue to final result
      }

      return false;

    } catch (error) {
      return false;
    }
  }

  // Pre-populate cache for immediate filtering
  private prePopulateCache(): void {
    // Pre-calculate filtered categories for all navigation tabs with proper ordering
    this.titles.forEach(mainTab => {
      if (mainTab.children && mainTab.children.length > 0) {
        mainTab.children.forEach(subTab => {
          if (subTab.categoryIds && subTab.categoryIds.length > 0) {
            const cacheKey = `${subTab.id}_${subTab.categoryIds.join('_')}`;
            const categoryIdsSet = new Set(subTab.categoryIds);
            const filtered = this.categories.filter(category =>
              categoryIdsSet.has(category.id)
            );

            // Apply proper ordering like in updateFilteredCategories
            const orderedFiltered = subTab.categoryIds
              .map((id: number) => filtered.find(category => category.id === id))
              .filter((category: any) => category !== undefined);

            this.categoriesCache.set(cacheKey, orderedFiltered);
          }
        });
      } else if (mainTab.categoryIds && mainTab.categoryIds.length > 0) {
        const cacheKey = `${mainTab.id}_${mainTab.categoryIds.join('_')}`;
        const categoryIdsSet = new Set(mainTab.categoryIds);
        const filtered = this.categories.filter(category =>
          categoryIdsSet.has(category.id)
        );

        // Apply proper ordering like in updateFilteredCategories
        const orderedFiltered = mainTab.categoryIds
          .map((id: number) => filtered.find(category => category.id === id))
          .filter((category: any) => category !== undefined);

        this.categoriesCache.set(cacheKey, orderedFiltered);
      }
    });
  }

  // Helper method for debug display to avoid template expression errors
  getChildrenNames(): string {
    if (!this.hoveredCategory?.children || this.hoveredCategory.children.length === 0) {
      return 'none';
    }
    return this.hoveredCategory.children.map((c: NavigationTab) => c.name).join(', ');
  }

  onMouseEnterSubcategory(subcategory: any): void {
    this.hoveredSubcategory = subcategory;
  }

  // Link click handlers that close the menu
  onLinkClick(): void {
    this.close.emit();
    this.expandedCategories.clear(); // Reset all expansions
    this.expandedSidebarCategories.clear(); // Reset sidebar expansions
  }

  onSubcategoryClick(subcategory: any): void {
    // Check if this subcategory is from the SALE tab
    const isFromSalesTab = this.hoveredCategory?.id === -1 && this.hoveredCategory?.name === 'SALE';

    // Check if this subcategory is from the NEW tab
    const isFromNewTab = this.hoveredCategory?.id === -2 && this.hoveredCategory?.name === 'NEW';

    // Navigate to the products page with the subcategory filter
    const queryParams: any = { sub_category: subcategory.id };

    // If from SALES tab, add discount filter
    if (isFromSalesTab) {
      queryParams.discount = 'true';
    }

    // If from NEW tab, add new products filter
    if (isFromNewTab) {
      queryParams.isNew = 'true';
    }

    this.router.navigate(['/products'], { queryParams });

    this.currentCategory = subcategory;
    this.expandedCategories.clear(); // Reset all expansions
    this.expandedSidebarCategories.clear(); // Reset sidebar expansions
    this.close.emit();
  }



  onMobileCategoryClick(category: any): void {
    this.currentCategory = category;
    // Don't close menu for mobile category navigation, just set current category
  }

  handleCategoryClick(category: any): void {
    // If category has children, toggle expansion
    if (category.children && category.children.length > 0) {
      this.toggleCategoryExpansion(category.id);
    } else {
      // If no children, navigate directly to category
      // Check if this category is from the SALE tab
      const isFromSalesTab = this.hoveredCategory?.id === -1 && this.hoveredCategory?.name === 'SALE';

      // Check if this category is from the NEW tab
      const isFromNewTab = this.hoveredCategory?.id === -2 && this.hoveredCategory?.name === 'NEW';

      // Navigate to the products page with the category filter
      const queryParams: any = { category: category.id };

      // If from SALES tab, add discount filter
      if (isFromSalesTab) {
        queryParams.discount = 'true';
      }

      // If from NEW tab, add new products filter
      if (isFromNewTab) {
        queryParams.isNew = 'true';
      }

      this.router.navigate(['/products'], { queryParams });

      this.currentCategory = category;
      this.expandedCategories.clear(); // Reset all expansions
      this.close.emit();
    }
  }

  toggleMobileCategory(tab: any): void {
    // For mobile, just set the hovered category without selecting a sub-tab
    this.hoveredCategory = tab;
    this.updateDesktopSections();
    this.selectedSubTab = null; // Don't auto-select first sub-tab on mobile
    this.currentCategory = tab;
    this.expandedCategories.clear();
    this.expandedSidebarCategories.clear();
    this.updateFilteredCategories();
    this.cdr.markForCheck();
  }

  goBackToMainCategories(): void {
    this.hoveredCategory = null;
    this.selectedSubTab = null;
    this.expandedCategories.clear(); // Reset all expansions
  }

  goBackFromCategories(): void {
    if (this.selectedSubTab) {
      // Go back to sub-tabs level
      this.selectedSubTab = null;
      this.onCategoryClick(this.hoveredCategory);
    } else {
      // Go back to main categories
      this.hoveredCategory = null;
      this.selectedSubTab = null;
    }
  }

  // Ensure first tab is selected on desktop for better UX
  private ensureFirstTabSelected(): void {
    if (this.titles && this.titles.length > 0 && !this.hoveredCategory) {
      this.hoveredCategory = this.titles[0];
      this.updateDesktopSections();
      if (this.titles[0].children && this.titles[0].children.length > 0) {
        this.selectedSubTab = this.titles[0].children[0];
        this.currentCategory = this.titles[0].children[0];
      } else {
        this.currentCategory = this.titles[0];
      }
      this.updateFilteredCategories();
      this.cdr.markForCheck();
    }
  }

  resetMobileNavigationState(): void {
    // Reset all mobile navigation state to start at main categories level
    this.hoveredCategory = null;
    this.selectedSubTab = null;
    this.expandedCategories.clear();
    this.expandedSidebarCategories.clear();
    this.currentCategory = null;
    this.cdr.markForCheck();
  }

  onMobileSubcategoryClick(subcategory: any): void {
    this.currentCategory = subcategory;
    this.close.emit();
  }

  // Toggle category expansion in mobile sidebar
  toggleCategoryExpansion(categoryId: string): void {
    if (this.expandedSidebarCategories.has(categoryId)) {
      this.expandedSidebarCategories.delete(categoryId);
    } else {
      this.expandedSidebarCategories.add(categoryId);
    }
  }

  // Check if category is expanded in mobile sidebar
  isExpanded(categoryId: string): boolean {
    return this.expandedSidebarCategories.has(categoryId);
  }

  // Toggle subcategory expansion on mobile
  toggleSubcategories(categoryId: string): void {
    if (this.expandedCategories.has(categoryId)) {
      this.expandedCategories.delete(categoryId);
    } else {
      this.expandedCategories.add(categoryId);
    }
  }

  // Check if subcategories are expanded
  isSubcategoriesExpanded(categoryId: string): boolean {
    return this.expandedCategories.has(categoryId);
  }

  // Slider methods
  startSlideshow(): void {
    this.slideInterval = setInterval(() => {
      this.nextSlide();
    }, 4000); // Change slide every 4 seconds
  }

  goToSlide(index: number): void {
    this.currentSlideIndex = index;
    this.resetSlideshow();
  }

  nextSlide(): void {
    this.currentSlideIndex = (this.currentSlideIndex + 1) % this.sliderImages.length;
  }

  previousSlide(): void {
    this.currentSlideIndex = this.currentSlideIndex === 0
      ? this.sliderImages.length - 1
      : this.currentSlideIndex - 1;
    this.resetSlideshow();
  }

  private resetSlideshow(): void {
    if (this.slideInterval) {
      clearInterval(this.slideInterval);
    }
    if (!this.isHovering) {
      this.startSlideshow();
    }
  }

  pauseSlideshow(): void {
    this.isHovering = true;
    if (this.slideInterval) {
      clearInterval(this.slideInterval);
    }
  }

  resumeSlideshow(): void {
    this.isHovering = false;
    this.startSlideshow();
  }

  handleTouchStart(event: TouchEvent): void {
    this.touchStartX = event.touches[0].clientX;
  }

  handleTouchEnd(event: TouchEvent): void {
    this.touchEndX = event.changedTouches[0].clientX;
    this.handleSwipe();
  }

  private handleSwipe(): void {
    const swipeThreshold = 50;
    const diff = this.touchStartX - this.touchEndX;

    if (Math.abs(diff) > swipeThreshold) {
      if (diff > 0) {
        this.nextSlide();
      } else {
        this.previousSlide();
      }
      this.resetSlideshow();
    }
  }

  private updateResponsiveStates() {
    this.isDesktop = window.innerWidth >= 1024;
    this.isMobile = window.innerWidth < 1024;
  }

  // Add trackBy functions for better performance
  trackByTitle(index: number, item: NavigationTab): string {
    return item.id ? String(item.id) : item.name;
  }

  trackByCategory(index: number, item: any): number {
    return item.id;
  }

  trackByChild(index: number, item: any): number {
    return item.id;
  }

  trackByProduct(index: number, product: any): string | number {
    return product?.id ?? index;
  }

  trackByImage(index: number, item: string): string {
    return item;
  }

  onNavigationTabImageError(event: Event): void {
    const image = event.target;
    if (image instanceof HTMLImageElement) {
      image.hidden = true;
    }
  }

  // Search functionality methods
  onSearchInput(event: any): void {
    this.searchQuery = event.target.value;
  }

  clearSearch(): void {
    this.searchQuery = '';
  }

  performSearch(): void {
    if (this.searchQuery.trim()) {
      // Navigate to search results or emit search event
      this.router.navigate(['/search'], {
        queryParams: { q: this.searchQuery.trim() }
      });
      this.close.emit();
    }
  }

  // Country selector methods
  onCountryButtonClick(event: Event): void {
    event.stopPropagation();
    this.countryClick.emit(event);
    this.isCountrySelectorOpen = !this.isCountrySelectorOpen;
  }

  // Auth dropdown methods
  toggleAuthDropdown(): void {
    this.isAuthDropdownOpen = !this.isAuthDropdownOpen;
  }

  handleProfileButtonClick(): void {
    if (this.isLoggedIn()) {
      // If user is logged in, emit profile click to navigate to profile and close sidebar
      this.profileClick.emit();
      this.close.emit();
    } else {
      // If user is not logged in, show auth dropdown
      this.toggleAuthDropdown();
    }
  }

  goToSignIn(): void {
    this.isAuthDropdownOpen = false;
    this.router.navigate(['/auth/login']);
  }

  goToSignUp(): void {
    this.isAuthDropdownOpen = false;
    this.router.navigate(['/auth/register-multi']);
  }

  isLoggedIn(): boolean {
    return this.isUserLoggedIn;
  }

  getInitials(user: any): string {
    if (!user) return 'U';
    const firstName = user.firstName || '';
    const lastName = user.lastName || '';

    if (firstName && lastName) {
      return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
    } else if (firstName) {
      return firstName.charAt(0).toUpperCase();
    } else if (lastName) {
      return lastName.charAt(0).toUpperCase();
    } else if (user.email) {
      return user.email.charAt(0).toUpperCase();
    } else if (user.username) {
      return user.username.charAt(0).toUpperCase();
    }
    return 'U';
  }

  changeLanguage(lang: string): void {
    if (this.isAnimating || this.currentLanguage === lang) {
      return; // Don't switch if already switching or already selected
    }

    this.isAnimating = true;
    this.languageService.switchLanguage(lang);
    this.currentLanguage = lang;
    setTimeout(() => {
      this.isAnimating = false;
      this.cdr.markForCheck();
    }, 500);
  }

}
